import clsx from 'clsx';
import { useLocation } from '@remix-run/react';
import type { LinksFunction } from '@remix-run/node';
import type { FC } from 'react';

import { getDestination } from '@/utils/getDestination';

import {
  SharedAnchor,
  links as sharedAnchorLinks,
} from '@/components/shared/shared-anchor/shared-anchor';
import {
  SharedDisclaimer,
  links as sharedDisclaimerLinks,
} from '@/components/shared/shared-disclaimer/shared-disclaimer';
import {
  SharedSimpleFooter,
  links as sharedSimpleFooterLinks,
} from '@/components/shared/shared-simple-footer/shared-simple-footer';
import { SharedIcon } from '../shared-icon/shared-icon';
import {
  SharedStoreButton,
  links as sharedStoreButtonLinks,
} from '../shared-store-button/shared-store-button';

import iconAuraLogoDark from '@/img/icon-aura-logo-dark.svg?url';

import type {
  SharedFooterProps,
  SharedFooterSocialNetwork,
} from './shared-footer.types';

import styles from './shared-footer.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  ...sharedAnchorLinks(),
  ...sharedDisclaimerLinks(),
  ...sharedSimpleFooterLinks(),
  ...sharedStoreButtonLinks(),
];

export const SharedFooter: FC<SharedFooterProps> = ({
  theme = 'dark',
  withDisclaimer,
  transparent,
  withStoresButton = true,
}) => {
  const location = useLocation();

  const classes = clsx('shared-footer', {
    'shared-footer--dark': theme === 'dark',
    'shared-footer--light': theme === 'light',
    'shared-footer--dboo': theme === 'dboo',
    'shared-footer--transparent': transparent,
    'shared-footer--with-stores-button': withStoresButton,
  });

  const socialNetworks: SharedFooterSocialNetwork[] = [
    {
      icon: 'linkedin',
      href: 'https://www.linkedin.com/company/auracompany/',
    },
    {
      icon: 'x',
      href: 'https://twitter.com/Aura_Protects',
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/aura_protects/',
    },
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/AuraProtects',
    },
    {
      icon: 'youtube',
      href: 'https://www.youtube.com/@AuraProtects',
    },
  ];

  const logoDestination = getDestination(location.pathname);

  return (
    <div className={classes}>
      <div className="shared-footer__wrapper">
        <div className="shared-footer__top-section">
          <div className="shared-footer__top-inner">
            <SharedAnchor
              href={logoDestination}
              className="shared-footer__logotype"
            >
              <img src={iconAuraLogoDark} alt="Aura Logo" />
            </SharedAnchor>
            <div className="shared-footer__social-networks">
              {socialNetworks.map(social => (
                <SharedAnchor
                  className="shared-footer__social-networks-item"
                  href={social.href}
                  key={social.icon}
                  isNewTab
                >
                  <SharedIcon name={social.icon} size={18} />
                </SharedAnchor>
              ))}
            </div>
          </div>
          {withStoresButton && (
            <div className="shared-footer__stores">
              <SharedStoreButton type="ios" theme={theme} />
              <SharedStoreButton type="google-play" theme={theme} />
            </div>
          )}
        </div>
        <div className="shared-footer__bottom-section">
          <SharedSimpleFooter theme={theme} />
          <div className="shared-footer__social-networks">
            {socialNetworks.map(social => (
              <SharedAnchor
                className="shared-footer__social-networks-item"
                href={social.href}
                key={social.icon}
                isNewTab
              >
                <SharedIcon name={social.icon} size={18} />
              </SharedAnchor>
            ))}
          </div>
        </div>
        {withDisclaimer && <SharedDisclaimer />}
      </div>
    </div>
  );
};
